// Icons and small UI atoms of the comparison result page.
import React from 'react';

/* ==========================================================
   Icons
   ========================================================== */
const cx = 'w-[18px] h-[18px]';
export const IconScale = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v18" /><path d="M5 7h14" />
    <path d="m7 7-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Zm10 0-4 7a4 4 0 0 0 4 4 4 4 0 0 0 4-4l-4-7Z" />
  </svg>
);
export const IconArrow = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h18" /><path d="M15 6l6 6-6 6" />
  </svg>
);
export const IconBadge = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2 20 7v7c0 5-4 8-8 8s-8-3-8-8V7Z" /><path d="M9 11l2 2 4-4" />
  </svg>
);
export const IconShare = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" />
    <path d="M16 6l-4-4-4 4" /><path d="M12 2v14" />
  </svg>
);
export const IconHeart = ({ filled = false }: { filled?: boolean }) => (
  <svg viewBox="0 0 24 24" className={cx} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
  </svg>
);
export const IconBeaker = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 2h12M9 2v5l-5 9a4 4 0 0 0 3.5 6h9a4 4 0 0 0 3.5-6L15 7V2" />
    <path d="M8 14h8" />
  </svg>
);
export const IconChart = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M4 19h16M8 18V9M12 18V5M16 18v-7" />
  </svg>
);
export const IconWarn = () => (
  <svg viewBox="0 0 24 24" className={cx} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M10.3 2.7 1.6 17.7A2 2 0 0 0 3.3 21h17.4a2 2 0 0 0 1.7-3.3L13.7 2.7a2 2 0 0 0-3.4 0Z" />
    <path d="M12 8v5M12 17h.01" />
  </svg>
);

/* ==========================================================
   Small UI atoms
   ========================================================== */
export function Chip({
  children,
  tone = 'neutral',
  block = false,
}: { children: React.ReactNode; tone?: 'success' | 'warn' | 'danger' | 'neutral'; block?: boolean }) {
  const mod =
    tone === 'success' ? 'pill--teal' :
    tone === 'warn'    ? 'pill--coral' :
    tone === 'danger'  ? 'pill--coral' : '';
  return (
    <span className={['pill', mod, block ? 'pill--block' : ''].filter(Boolean).join(' ')}>
      {children}
    </span>
  );
}

export function Badge({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'success' | 'warn' | 'danger' | 'neutral' }) {
  const mod =
    tone === 'success' ? 'pill--teal' :
    tone === 'warn'    ? 'pill--coral' :
    tone === 'danger'  ? 'pill--coral' : '';
  return (
    <span className={['pill pill--compact', mod].filter(Boolean).join(' ')}>{children}</span>
  );
}

export function SectionCard({
  title, children, icon,
}: {
  title: string; children: React.ReactNode; icon?: React.ReactNode;
}) {
  return (
    <section className="card card--bordered stack-4" style={{ padding: 'var(--s-5)', overflow: 'hidden' }}>
      <div className="row-3">
        {icon && <span style={{ color: 'var(--ink-muted)', flexShrink: 0 }}>{icon}</span>}
        <h3 className="t-small" style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>{title}</h3>
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </section>
  );
}

export function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card card--compact card--bordered card--flat" style={{ minWidth: 0 }}>
      <div className="t-small t-mute">{label}</div>
      <div className="t-small" style={{ fontWeight: 600, color: 'var(--ink)', wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

/* ---------------- Score ring ---------------- */
function scoreTone(score: number) {
  if (score >= 75) return { label: 'Strong',   color: 'var(--c-kivo-teal)', stroke: 'var(--c-kivo-teal)' };
  if (score >= 50) return { label: 'Moderate', color: 'var(--c-deep-teal)', stroke: 'var(--c-deep-teal)' };
  if (score >= 30) return { label: 'Caution',  color: 'var(--c-coral)',     stroke: 'var(--c-coral)' };
  return              { label: 'Flagged',  color: 'var(--c-coral)',     stroke: 'var(--c-coral)' };
}
export function ScoreRing({ score, size = 140, stroke = 12 }: { score: number; size?: number; stroke?: number }) {
  const pct = Math.max(0, Math.min(100, score));
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const dash = C * (pct / 100);
  const tone = scoreTone(pct);
  return (
    <div className="score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: 'block' }}>
        <circle cx={size / 2} cy={size / 2} r={r} style={{ stroke: 'var(--rule)' }} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          style={{ stroke: tone.stroke }}
          strokeWidth={stroke} strokeLinecap="round" fill="none"
          strokeDasharray={`${dash} ${C - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="score-ring__inner">
        <div className="score-ring__num" style={{ color: tone.color }}>{Math.round(pct)}</div>
        <div className="t-label" style={{ marginTop: 0 }}>Score</div>
        <div className="t-small t-mute">{tone.label}</div>
      </div>
    </div>
  );
}
